import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Rocket, Mail, Lock, User, Phone, MapPin } from "lucide-react";

const STAGES = ["Idea", "Research", "Prototype", "MVP", "Product Ready", "Revenue Generating", "Scaling"];
const INDIAN_STATES = ["Telangana", "Andhra Pradesh", "Karnataka", "Maharashtra", "Delhi", "Tamil Nadu", "Gujarat", "West Bengal", "Kerala", "Rajasthan", "Uttar Pradesh", "Other"];

export default function PortalAuth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    name: "", phone: "", email: "", password: "", city: "", state: "", country: "India",
    founder_status: "Founder", entity_type: "Individual", industry: "", startup_stage: "Idea", referral_code: "",
  });
  const setReg = (k, v) => setRegForm((f) => ({ ...f, [k]: v }));
  const [searchParams] = useSearchParams();
  const claimServiceId = searchParams.get("service");
  const [claimName, setClaimName] = useState("");

  useEffect(() => {
    const attr = {
      service_id: claimServiceId || "",
      utm_source: searchParams.get("utm_source") || "",
      utm_medium: searchParams.get("utm_medium") || "",
      utm_campaign: searchParams.get("utm_campaign") || "",
      landing: "/services",
    };
    if (attr.service_id || attr.utm_source || attr.utm_campaign) {
      sessionStorage.setItem("ll_attr", JSON.stringify(attr));
    }
    const ref = searchParams.get("ref");
    if (ref) {
      setRegForm((f) => ({ ...f, referral_code: ref }));
      setMode("register");
    }
    if (!claimServiceId) return;
    api.get("/portal/services")
      .then((r) => {
        const sv = r.data.find((x) => x.id === claimServiceId);
        setClaimName(sv ? sv.name : "");
      })
      .catch(() => {});
  }, [claimServiceId, searchParams]);

  const afterAuth = (user) => {
    if (claimServiceId) sessionStorage.setItem("ll_claim_service", claimServiceId);
    navigate(user.role === "specialist" ? "/specialist" : "/dashboard");
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/portal/auth/login", loginForm);
      afterAuth(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let attribution = null;
      try { attribution = JSON.parse(sessionStorage.getItem("ll_attr") || "null"); } catch { attribution = null; }
      const res = await api.post("/portal/auth/register", { ...regForm, ...(attribution ? { attribution } : {}) });
      sessionStorage.removeItem("ll_attr");
      afterAuth(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grid-tech flex items-center justify-center px-4 py-10" data-testid="portal-auth-page">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(255,136,0,0.08),transparent_55%)]" />
      <div className="glass relative w-full max-w-lg rounded-2xl p-8 sm:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
        <Link to="/" className="flex items-center gap-3 mb-7">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center shadow-[0_0_30px_rgba(255,136,0,0.35)]">
            <Rocket className="w-5 h-5 text-[#06080E]" strokeWidth={2.4} />
          </div>
          <div>
            <p className="font-display font-bold text-xl tracking-wide text-white">LAUNCHLAB <span className="text-[#FF8800]">PORTAL</span></p>
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-400 uppercase">Founder & Specialist Access</p>
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-2 mb-7 glass rounded-xl p-1.5" role="tablist">
          {["login", "register"].map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              data-testid={`portal-tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                mode === m ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "text-slate-400 hover:text-white"
              }`}
            >
              {m === "login" ? "Login" : "Register as Founder"}
            </button>
          ))}
        </div>

        {claimName && (
          <div data-testid="portal-claim-note" className="mb-5 text-sm text-[#00E676] bg-[#00E676]/10 border border-[#00E676]/30 rounded-lg px-3 py-2">
            You're continuing with: <span className="font-semibold">{claimName}</span> — sign in or create your account to add it to your plan.
          </div>
        )}

        {error && (
          <div data-testid="portal-auth-error" className="mb-5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {mode === "login" ? (
          <form onSubmit={onLogin} className="space-y-5" data-testid="portal-login-form">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="portal-login-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@company.com" className="pl-10 bg-[#0D111D] border-white/10 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="portal-login-password" type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" className="pl-10 bg-[#0D111D] border-white/10 text-white" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="portal-login-submit" className="w-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold h-11">
              {loading ? "Signing in..." : "Login to Portal"}
            </Button>
            <p className="text-xs text-slate-500 text-center">Specialists use the same login with their provided credentials.</p>
          </form>
        ) : (
          <form onSubmit={onRegister} className="space-y-4 max-h-[58vh] overflow-y-auto pr-1" data-testid="portal-register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input data-testid="portal-reg-name" required value={regForm.name} onChange={(e) => setReg("name", e.target.value)} className="pl-10 bg-[#0D111D] border-white/10 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Mobile Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input data-testid="portal-reg-phone" required value={regForm.phone} onChange={(e) => setReg("phone", e.target.value)} className="pl-10 bg-[#0D111D] border-white/10 text-white" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Email *</Label>
              <Input data-testid="portal-reg-email" type="email" required value={regForm.email} onChange={(e) => setReg("email", e.target.value)} className="bg-[#0D111D] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Password * (min 8 characters)</Label>
              <Input data-testid="portal-reg-password" type="password" required minLength={8} value={regForm.password} onChange={(e) => setReg("password", e.target.value)} className="bg-[#0D111D] border-white/10 text-white" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">City</Label>
                <Input data-testid="portal-reg-city" value={regForm.city} onChange={(e) => setReg("city", e.target.value)} className="bg-[#0D111D] border-white/10 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">State</Label>
                <Select value={regForm.state} onValueChange={(v) => setReg("state", v)}>
                  <SelectTrigger data-testid="portal-reg-state" className="bg-[#0D111D] border-white/10 text-slate-200"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-56">
                    {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">I am a</Label>
                <Select value={regForm.founder_status} onValueChange={(v) => setReg("founder_status", v)}>
                  <SelectTrigger data-testid="portal-reg-founder-status" className="bg-[#0D111D] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                    {["Founder", "Co-Founder", "Founding Team"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Entity</Label>
                <Select value={regForm.entity_type} onValueChange={(v) => setReg("entity_type", v)}>
                  <SelectTrigger data-testid="portal-reg-entity" className="bg-[#0D111D] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                    {["Individual", "Existing Company"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Industry</Label>
                <Input data-testid="portal-reg-industry" value={regForm.industry} onChange={(e) => setReg("industry", e.target.value)} placeholder="e.g. Food, SaaS" className="bg-[#0D111D] border-white/10 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Startup Stage</Label>
                <Select value={regForm.startup_stage} onValueChange={(v) => setReg("startup_stage", v)}>
                  <SelectTrigger data-testid="portal-reg-stage" className="bg-[#0D111D] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Referral Code (optional)</Label>
              <Input data-testid="portal-reg-referral" value={regForm.referral_code} onChange={(e) => setReg("referral_code", e.target.value)} className="bg-[#0D111D] border-white/10 text-white" />
            </div>
            <Button type="submit" disabled={loading} data-testid="portal-register-submit" className="w-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold h-11">
              {loading ? "Creating account..." : "Create Founder Account"}
            </Button>
          </form>
        )}

        <Link to="/" data-testid="portal-back-home" className="block text-center mt-6 text-xs text-slate-500 hover:text-slate-300 transition-colors">
          ← Back to launchlab.in
        </Link>
      </div>
    </div>
  );
}
